import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { APP_PATHS, buildLocationReturnUrl, buildLoginUrl } from '@/lib/navigation';
import {
  AUTH_SESSION_EXPIRED_EVENT,
  AuthApi,
  clearAuthTokens,
  getAuthRole,
  getAuthRoles,
  getAuthToken,
  getAuthUid,
  getOrCreateDeviceId,
  saveAuthTokens,
} from '@/services/auth';
import type { AuthApiError, AuthContextValue } from '@/types/auth';
import { AUTH_ERROR_MESSAGES, UserAccountStatus } from '@/types/auth';
import type { UserTeamRole } from '@/types/team';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const DAILY_ACCESS_DATE_STORAGE_KEY = 'manclaw_daily_access_verified_date';

interface AuthProviderProps {
  children: React.ReactNode;
}

function getShanghaiDateKey(date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${byType.year}-${byType.month}-${byType.day}`;
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

function getDailyAccessStorageKey(uid: number): string {
  return `${DAILY_ACCESS_DATE_STORAGE_KEY}:${uid}`;
}

function readRecordedDailyAccessDate(uid: number): string | null {
  try {
    return window.localStorage.getItem(getDailyAccessStorageKey(uid));
  } catch {
    return null;
  }
}

function writeRecordedDailyAccessDate(uid: number, dateKey: string): void {
  try {
    window.localStorage.setItem(getDailyAccessStorageKey(uid), dateKey);
  } catch {
    // Ignore storage failures; the server still deduplicates by user and date.
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [uid, setUid] = useState<number | null>(null);
  const [role, setRole] = useState<UserTeamRole>('none');
  const [roles, setRoles] = useState<UserTeamRole[]>([]);
  const location = useLocation();
  const navigate = useNavigate();

  // 初始化：从 Cookie 读取 token 和 uid
  useEffect(() => {
    const token = getAuthToken();
    const authUid = getAuthUid();
    if (token && authUid) {
      setIsAuthenticated(true);
      setUid(authUid);
      setRole(getAuthRole());
      setRoles(getAuthRoles());
    } else {
      setIsAuthenticated(false);
      setUid(null);
      setRole('none');
      setRoles([]);
    }

    setIsLoading(false);
  }, []);

  const login = useCallback(
    async (phone: string, code: string, invitationCode?: string): Promise<void> => {
      try {
        const deviceId = getOrCreateDeviceId();
        const response = await AuthApi.login(phone, code, deviceId, invitationCode);

        const status = response.user.status ?? UserAccountStatus.ACTIVE;

        const nextRoles = response.user.roles ?? [];
        const nextRole = response.user.role ?? nextRoles[0] ?? 'none';

        saveAuthTokens(
          response.token,
          response.user.uid,
          response.expires_in,
          nextRole,
          nextRoles,
          status,
        );
        setIsAuthenticated(true);
        setUid(response.user.uid);
        setRole(nextRole);
        setRoles(nextRoles.filter((item) => item !== 'none'));
      } catch (error) {
        const authError = error as AuthApiError;
        if (authError.code && AUTH_ERROR_MESSAGES[authError.code]) {
          throw new Error(AUTH_ERROR_MESSAGES[authError.code]);
        }
        throw error;
      }
    },
    [],
  );

  const clearSession = useCallback((): void => {
    clearAuthTokens();
    setIsAuthenticated(false);
    setUid(null);
    setRole('none');
    setRoles([]);
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    const token = getAuthToken();
    const authUid = getAuthUid();

    // 尝试调用登出 API，失败不阻塞流程
    if (token && authUid) {
      try {
        await AuthApi.logout(token, authUid);
      } catch {
        // 忽略登出 API 调用失败
      }
    }

    clearSession();
    navigate(APP_PATHS.landing, { replace: true });
  }, [clearSession, navigate]);

  const recordDailyAccess = useCallback(async (): Promise<void> => {
    if (!isAuthenticated || !uid) return;
    if (document.visibilityState === 'hidden') return;

    const token = getAuthToken();
    const authUid = getAuthUid();
    if (!token || !authUid) return;

    const dateKey = getShanghaiDateKey();
    if (readRecordedDailyAccessDate(authUid) === dateKey) return;

    try {
      await AuthApi.verifyToken(token, authUid, { awardDailyAccess: true });
      writeRecordedDailyAccessDate(authUid, dateKey);
      window.dispatchEvent(new CustomEvent('points:daily-access-recorded'));
    } catch {
      // Regular API auth handling will surface invalid sessions; daily access is best effort.
    }
  }, [isAuthenticated, uid]);

  // 会话失效时保留当前工作台地址，重新登录后可回到原位置。
  useEffect(() => {
    const handleSessionExpired = () => {
      const returnUrl = buildLocationReturnUrl(location);
      clearSession();
      navigate(buildLoginUrl(returnUrl), { replace: true });
    };

    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);

    return () => {
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [clearSession, location, navigate]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !uid) return;

    const handleForegroundAccess = () => {
      void recordDailyAccess();
    };

    handleForegroundAccess();
    window.addEventListener('focus', handleForegroundAccess);
    document.addEventListener('visibilitychange', handleForegroundAccess);

    return () => {
      window.removeEventListener('focus', handleForegroundAccess);
      document.removeEventListener('visibilitychange', handleForegroundAccess);
    };
  }, [isAuthenticated, isLoading, recordDailyAccess, uid]);

  const sendCode = useCallback(async (phone: string, invitationCode?: string): Promise<void> => {
    try {
      await AuthApi.sendCode(phone, invitationCode);
    } catch (error) {
      const authError = error as AuthApiError;
      if (authError.code && AUTH_ERROR_MESSAGES[authError.code]) {
        throw new Error(AUTH_ERROR_MESSAGES[authError.code]);
      }
      throw error;
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        uid,
        role,
        roles,
        isAdmin: roles.includes('admin') || role === 'admin',
        isLeader:
          roles.includes('admin') ||
          roles.includes('leader') ||
          role === 'admin' ||
          role === 'leader',
        login,
        logout,
        sendCode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
